import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const current = categories.find((c) => c.slug === slug) || categories[0];
  const filtered = articles.filter((a) => a.category === current.slug);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${current.name} - 鲍鱼网址域名导航`,
      description: current.description,
      url: `https://baoyu.com/category/${current.slug}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '鲍鱼网址域名导航',
        url: 'https://baoyu.com',
      },
    });
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, [current]);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[{ label: '分类导航' }, { label: current.name }]} />

        <section className="px-4 md:px-6 py-8 md:py-10 bg-background-50 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                current.color === 'primary' ? 'bg-primary-100 text-primary-600' :
                current.color === 'accent' ? 'bg-accent-100 text-accent-600' :
                'bg-secondary-100 text-secondary-600'
              }`}>
                <i className={`${current.icon} text-lg`}></i>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">{current.name}</h1>
            </div>
            <p className="text-foreground-500 text-sm mt-2">{current.description}</p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="px-4 md:px-6 py-5 bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    current.slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Article List */}
        <section className="px-4 md:px-6 py-10 bg-background-100">
          <div className="max-w-6xl mx-auto">
            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        {article.tags.map((tag) => (
                          <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap">{tag}</span>
                        ))}
                      </div>
                      <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-base leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2">{article.excerpt}</p>
                      <div className="mt-4 flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.date}</span>
                        <div className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          <span>{article.readTime}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                  <i className="ri-file-list-3-line text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章，请查看其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}